import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Alert, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, Button,
} from '@mui/material';
import { X, History, QrCode, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import api from '../config/api';
import {
  StyledTableContainer, TableSkeleton, FilterBar, SearchInput,
  StyledPagination, EmptyState, StatusChip,
} from '../components/TableShared';

const ff = "'Poppins', sans-serif";

const formatDate = (d) => {
  if (!d) return 'N/A';
  try { return format(new Date(d), 'MMM dd, yyyy'); } catch { return 'N/A'; }
};
const formatDateTime = (d) => {
  if (!d) return 'N/A';
  try { return format(new Date(d), 'MMM dd, yyyy hh:mm a'); } catch { return 'N/A'; }
};

// ─── Scan History Dialog ─────────────────────────────────────
function ScanHistoryDialog({ open, pet, onClose }) {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [deleting, setDeleting] = useState(null);

  const fetchScans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/linked-qr/${encodeURIComponent(pet.qrCode)}/scans`, { params: { limit: 50 } });
      const data = res.data.data || res.data;
      setScans(data.scans || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Error fetching scan history:', e);
    } finally {
      setLoading(false);
    }
  }, [pet]);

  useEffect(() => {
    if (open && pet?.qrCode) {
      fetchScans();
    }
  }, [open, pet, fetchScans]);

  const handleDelete = async (scanId) => {
    if (!window.confirm('Are you sure you want to delete this scan record?')) return;
    try {
      setDeleting(scanId);
      await api.delete(`/admin/scans/${scanId}`);
      setScans(prev => prev.filter(s => s.id !== scanId));
      setTotal(prev => prev - 1);
    } catch (e) {
      console.error('Error deleting scan:', e);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <History size={20} color="#32A6D8" />
          Scan History - {pet?.name || 'Pet'}
        </Box>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Pet Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, background: '#F7FAFC', borderRadius: '10px' }}>
          <Avatar src={pet?.photoUrl} sx={{ width: 48, height: 48 }}>
            <QrCode size={20} />
          </Avatar>
          <Box>
            <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 600, color: '#1A202C' }}>{pet?.name}</Box>
            <Box sx={{ fontFamily: ff, fontSize: 12, color: '#718096' }}>{pet?.type} {pet?.breed ? `- ${pet.breed}` : ''}</Box>
            <Box sx={{ fontSize: 11, color: '#A0AEC0', fontFamily: 'monospace' }}>{pet?.qrCode}</Box>
          </Box>
          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
            <Box sx={{ fontFamily: ff, fontSize: 12, color: '#718096' }}>Owner</Box>
            <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 500, color: '#2D3748' }}>{pet?.user?.fullName || 'N/A'}</Box>
          </Box>
        </Box>

        {/* Scans Table */}
        <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 600, color: '#2D3748', mb: 1.5 }}>
          {total} scan{total !== 1 ? 's' : ''} recorded
        </Box>

        {loading ? (
          <TableSkeleton columns={6} rows={4} />
        ) : scans.length > 0 ? (
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Scanned By</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Scanned At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scans.map((scan) => (
                  <TableRow key={scan.id}>
                    <TableCell>
                      <Box sx={{ fontFamily: ff, fontSize: 13 }}>
                        {scan.scanner?.fullName || 'Anonymous'}
                      </Box>
                      {scan.scanner?.email && (
                        <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>{scan.scanner.email}</Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ fontFamily: ff, fontSize: 12, color: '#4A5568' }}>
                        {scan.address || (scan.latitude ? `${scan.latitude.toFixed(4)}, ${scan.longitude.toFixed(4)}` : 'N/A')}
                      </Box>
                    </TableCell>
                    <TableCell>{scan.city || 'N/A'}</TableCell>
                    <TableCell>{scan.country || 'N/A'}</TableCell>
                    <TableCell>{formatDateTime(scan.scannedAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(scan.id)}
                        disabled={deleting === scan.id}
                        sx={{ color: '#E53E3E', '&:hover': { background: '#FFF5F5' } }}
                      >
                        <Trash2 size={15} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        ) : (
          <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', textAlign: 'center', py: 4 }}>
            No scans recorded for this QR code yet.
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontFamily: ff, textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Linked QR Page ──────────────────────────────────────────
export default function LinkedQR() {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPets, setTotalPets] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const debounceTimer = useRef(null);

  // Scan history dialog
  const [historyPet, setHistoryPet] = useState(null);

  const fetchPets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: page + 1, limit: rowsPerPage };
      if (search) params.search = search;
      const res = await api.get('/admin/linked-qr', { params });
      const data = res.data.data || res.data;
      setPets(data.pets || []);
      setTotalPets(data.total || 0);
    } catch (e) {
      console.error('Error fetching linked QR pets:', e);
      setError('Failed to load linked QR codes.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => { fetchPets(); }, [fetchPets]);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(0);
    }, 300);
  }, []);

  useEffect(() => () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <FilterBar>
        <SearchInput
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search by pet name, QR code, breed..."
        />
        <Box sx={{ ml: 'auto', fontFamily: ff, fontSize: 12, color: '#A0AEC0', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <QrCode size={14} />
          {totalPets} QR code{totalPets !== 1 ? 's' : ''} linked
        </Box>
      </FilterBar>

      {loading ? (
        <TableSkeleton columns={7} rows={6} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Pet</TableCell>
                <TableCell>Type / Breed</TableCell>
                <TableCell>QR Code</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Scans</TableCell>
                <TableCell>Linked Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pets.map((pet) => (
                <TableRow key={pet.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={pet.photoUrl} sx={{ width: 34, height: 34, fontSize: 13 }}>
                        {pet.name?.[0]}
                      </Avatar>
                      <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 500, color: '#1A202C' }}>{pet.name}</Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ fontFamily: ff, fontSize: 13 }}>{pet.type || 'N/A'}</Box>
                    <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>{pet.breed || ''}</Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      fontSize: 11,
                      color: '#32A6D8',
                      background: '#EBF8FF',
                      px: 1,
                      py: 0.25,
                      borderRadius: '4px',
                      display: 'inline-block',
                      maxWidth: 140,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {pet.qrCode}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ fontFamily: ff, fontSize: 13 }}>{pet.user?.fullName || 'N/A'}</Box>
                    <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>{pet.user?.email || ''}</Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={`${pet.scanCount || 0} scan${(pet.scanCount || 0) !== 1 ? 's' : ''}`}
                      color={pet.scanCount > 0 ? '#38A169' : '#A0AEC0'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(pet.linkedAt || pet.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<History size={14} />}
                      onClick={() => setHistoryPet(pet)}
                      sx={{
                        fontFamily: ff,
                        fontSize: 11,
                        textTransform: 'none',
                        color: '#32A6D8',
                        '&:hover': { background: '#EBF8FF' },
                      }}
                    >
                      Scan History
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {pets.length === 0 && <EmptyState colSpan={7} message="No pets with linked QR codes found." />}
            </TableBody>
          </Table>
          <StyledPagination
            count={totalPets}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </StyledTableContainer>
      )}

      {/* Scan History Dialog */}
      <ScanHistoryDialog
        open={!!historyPet}
        pet={historyPet}
        onClose={() => setHistoryPet(null)}
      />
    </Box>
  );
}
